import { castArray, isEmpty, isFunction, isString, sortBy } from 'lodash-es';

import { WidgetConfig, WidgetConfigBase, WidgetConfigBaseWithExtensionPoint } from 'types';

import { registerService } from './register';

type WidgetConfigWithExtensionPoint = WidgetConfig & WidgetConfigBaseWithExtensionPoint;

const isWidgetConfigWithExtensionPoint = (
  widgetConfig: WidgetConfigBase,
): widgetConfig is WidgetConfigBaseWithExtensionPoint => {
  return !!widgetConfig.extensionPoint;
};

const initWidgetServices = () => {
  const widgetsStore = {};
  const extensionPointWidgetStore: Map<string, WidgetConfigWithExtensionPoint[]> = new Map();

  const registerWidget = (widgetConfig: WidgetConfig) => {
    const { name, bootstrap, mount, unmount } = widgetConfig;
    if (!isString(name) || isEmpty(name)) {
      throw Error('registerWidget: widget name must be a unique, non-empty string!');
    }
    if (widgetsStore[name]) {
      throw Error(`registerWidget(${name}): already registered - widget name must be unique!`);
    }
    if (!isFunction(bootstrap)) {
      throw Error(`registerWidget(${name}): unable to register - bootstrap method is missing!`);
    }
    if (!isFunction(mount)) {
      throw Error(`registerWidget(${name}): unable to register - mount method is missing!`);
    }
    if (!isFunction(unmount)) {
      throw Error(`registerWidget(${name}): unable to register - unmount method is missing!`);
    }

    widgetsStore[name] = widgetConfig;

    if (isWidgetConfigWithExtensionPoint(widgetConfig)) {
      const extensionPointName = widgetConfig.extensionPoint.name;
      const isExtensionPointNameInStore = extensionPointWidgetStore.has(extensionPointName);
      const currentItems = extensionPointWidgetStore.get(extensionPointName) ?? [];
      extensionPointWidgetStore.set(
        extensionPointName,
        castArray(isExtensionPointNameInStore ? sortBy([...currentItems, widgetConfig], 'order') : widgetConfig),
      );
    }
  };

  const getWidget = (name) => widgetsStore[name] || null;

  const getWidgetsStore = () => widgetsStore;

  const getWidgetsByExtensionPoint = (extensionPointName: string) => extensionPointWidgetStore.get(extensionPointName);

  const resetWidgets = () => {
    Object.keys(widgetsStore).forEach((key) => delete widgetsStore[key]);
  };

  registerService('registerWidget', registerWidget);
  registerService('getWidget', getWidget);
  registerService('getWidgetsStore', getWidgetsStore);
  registerService('getWidgetsByExtensionPoint', getWidgetsByExtensionPoint);
  registerService('resetWidgets', resetWidgets);
};
export default initWidgetServices;
